import { useState, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import { apiGet } from './apiClient';
import './App.css';

export default function UsageDashboardPage() {
  const { user } = useAuthContext();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const data = await apiGet(`/v1/users/${user.uid}/usage`);
        setUsage(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user) {
    return (
      <div style={{ maxWidth: 600, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', color: '#f8fafc', marginBottom: 12 }}>Usage Dashboard</h1>
        <p style={{ color: '#94a3b8' }}>Sign in to view your usage.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 700, margin: '60px auto', padding: '0 24px', color: '#94a3b8', textAlign: 'center' }}>
        Loading usage data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 700, margin: '60px auto', padding: '0 24px', textAlign: 'center' }}>
        <p style={{ color: '#f87171' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700, margin: '40px auto', padding: '0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', color: '#f8fafc', margin: 0 }}>Usage Dashboard</h1>
        <span style={{
          padding: '4px 12px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
          background: usage.tier === 'free' ? '#334155' : '#1e3a5f',
          color: usage.tier === 'free' ? '#94a3b8' : '#3b82f6',
        }}>
          {usage.tier.toUpperCase()}
        </span>
      </div>

      {/* Meters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <MeterCard
          title="Compute Tokens"
          value={usage.tokens.balance.toFixed(1)}
          subtitle={`${usage.tokens.lifetime.toFixed(0)} lifetime`}
          color="#3b82f6"
        />
        <MeterCard
          title="Storage"
          value={`${usage.storage.usedMB} MB`}
          subtitle={`of ${usage.storage.quotaMB} MB`}
          percent={usage.storage.percentUsed}
          color={usage.storage.percentUsed > 80 ? '#f87171' : '#4ade80'}
        />
        <MeterCard
          title="Bandwidth"
          value={`${usage.bandwidth.usedMB} MB`}
          subtitle={`of ${usage.bandwidth.capMB} MB (${usage.bandwidth.month})`}
          percent={usage.bandwidth.percentUsed}
          color={usage.bandwidth.percentUsed > 80 ? '#f87171' : '#4ade80'}
        />
      </div>

      {/* Alerts */}
      {usage.storage.percentUsed > 80 && (
        <Alert message={`Storage is ${usage.storage.percentUsed}% full. Consider upgrading your plan.`} />
      )}
      {usage.bandwidth.percentUsed > 80 && (
        <Alert message={`Bandwidth is ${usage.bandwidth.percentUsed}% used this month. Upgrade or deploy to Vercel for unlimited bandwidth.`} />
      )}

      {/* Recent Transactions */}
      <h2 style={{ fontSize: '1.1rem', color: '#f8fafc', marginBottom: 12 }}>Recent Token Activity</h2>
      {usage.recentTransactions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#64748b', padding: 32 }}>
          No token activity yet
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          {usage.recentTransactions.map((tx, i) => (
            <div key={tx.id} style={{
              padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: i < usage.recentTransactions.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#f8fafc', fontWeight: 500 }}>
                  {tx.type === 'purchase' ? 'Token Purchase' : tx.model || tx.type}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ''}
                  {tx.provider ? ` · ${tx.provider}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: '0.85rem', fontWeight: 600,
                  color: tx.type === 'purchase' ? '#4ade80' : '#f8fafc',
                }}>
                  {tx.type === 'purchase' ? '+' : '-'}{Math.abs(tx.amount).toFixed(1)} tokens
                </div>
                {tx.costUsd != null && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>${tx.costUsd.toFixed(2)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MeterCard({ title, value, subtitle, percent, color }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: percent != null ? 10 : 0 }}>{subtitle}</div>
      {percent != null && (
        <div style={{ height: 4, borderRadius: 2, background: '#0f172a' }}>
          <div style={{ height: 4, borderRadius: 2, background: color, width: `${Math.min(percent, 100)}%`, transition: 'width 0.3s' }} />
        </div>
      )}
    </div>
  );
}

function Alert({ message }) {
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 10, marginBottom: 16,
      background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)',
      color: '#fbbf24', fontSize: '0.85rem',
    }}>
      {message}
    </div>
  );
}

const cardStyle = {
  background: '#1e293b', borderRadius: 14, padding: 20,
  border: '1px solid rgba(255,255,255,0.08)',
};
